/**
 * 図の節点に載せる差分の印 — 地の色・上端の帯・削除の破線の枠。
 *
 * **変わった節点は、節点そのものを塗る。** 線の色と破線だけでは、面積の小さい線を目で追って
 * 「どの節点が変わったか」を探すことになる。案件から見たフローで実際にそうなっていた —
 * 変わった業務の節点には何の印も無く、見分けられるのは消えた枝の赤い破線だけだった。
 *
 * 色の割り当てはデザインの「色の役割分離」に従う。追加・変更は緑（地と上端の帯）、削除は
 * 赤（地と破線の枠）。帯と破線は表・一覧と同じ語彙（`specDiffNodeDecoration`）から引く。図ごとに
 * 色を決めると、同じ「追加」が画面によって違う色で出る。
 *
 * **種別のタグ（追加 ／ 変更 ／ 削除）は載せない**（Dhik 2026-09-29「業務フローも色でわかるから
 * タグいらんよ」）。節点の地の色と帯で変わったことが分かり、削除は破線の枠が形でも言う。タグは節点の
 * 右上にはみ出して隣の節点や線のラベルに重なり、左右比較の表から札を外したのとも揃わない。状態は
 * 節点の `data-change-state` に残る。
 *
 * **地は背景色に重ねる。** 地の色は透過色なので、節点の背景色を置き換えると下のレーン帯が
 * 透けて節点の輪郭が曖昧になる。背景色はそのままにし、その上に 1 枚重ねる。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 図の節点に、差分の状態に応じた地の色・上端の帯・削除の破線の枠を載せる。
 * @graph-connects specDiffNodeDecoration [calls] 差分の印の帯と枠の線種
 */
import type { CSSProperties } from 'react';

import { specDiffNodeDecoration } from '../../components/spec/SpecDiffState.js';
import type { FlowChangeState } from './flow-diff-marks.js';

/**
 * 状態ごとの地の色。追加・変更は緑、削除は赤。
 *
 * 濃さは表のセル（.15）ではなく、変わった語（.40）の段を使う。節点は語と同じく面積が小さく、
 * セルの濃さでは暗い背景の上でほとんど見えない（案件 139162 の画面で見づらいと指摘された）。
 * @graph-connects none
 */
const NODE_FILL: Readonly<Record<FlowChangeState, string>> = {
  created: 'var(--color-diff-added-word)',
  updated: 'var(--color-diff-added-word)',
  deleted: 'var(--color-diff-removed-word)',
};

/**
 * 節点に重ねる地の色の指定。変わっていない節点には何も重ねない。
 *
 * @param state 節点の差分の状態。変わっていなければ null
 * @returns 節点の `style` に足す指定
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 変わった節点の背景に、追加・変更は緑、削除は赤の地を重ねる指定を返す。
 * @graph-connects none
 */
export function flowNodeFillStyle(state: FlowChangeState | null): CSSProperties | undefined {
  if (state === null) return undefined;
  const fill = NODE_FILL[state];
  return { backgroundImage: `linear-gradient(${fill}, ${fill})` };
}

/**
 * 節点の枠に足す class（削除だけ破線）。変わっていない節点には何も足さない。
 *
 * @param state 節点の差分の状態。変わっていなければ null
 * @returns 枠に足す class
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 消えた節点の枠を破線にする class を返す。
 * @graph-connects specDiffNodeDecoration [calls] 差分の印の帯と枠の線種
 */
export function flowNodeBorderClass(state: FlowChangeState | null): string {
  return state === null ? '' : specDiffNodeDecoration(state).border;
}

/**
 * 節点の上端の帯。節点（`position: relative` の箱）の中に置く。
 *
 * 帯は「上に何かが足された」形なので追加と変更にだけ付け、削除には付けない（削除は破線の枠と
 * 赤い地で示す）。種別のタグは出さない（ファイル冒頭の決定）。
 *
 * @param props 節点の差分の状態
 * @returns 上端の帯。変わっていない節点と削除した節点には何も描かない
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 追加・変更した節点の上端に帯を引く。
 * @graph-connects specDiffNodeDecoration [calls] 差分の印の帯と枠の線種
 */
export function FlowNodeChangeMark({ state }: { state: FlowChangeState | null }) {
  if (state === null) return null;
  const { bar } = specDiffNodeDecoration(state);
  if (bar === null) return null;
  return <span aria-hidden className={`pointer-events-none absolute inset-x-0 top-0 h-1 ${bar}`} />;
}
